/**
 * Shared helpers for theme components.
 */

const MOBILE_QUERY = '(max-width: 767px)';
const REDUCED_MOTION_QUERY = '(prefers-reduced-motion: reduce)';
const EMAIL_PATTERN = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;

/**
 * @template {(...args: any[]) => void} T
 * @param {T} callback
 * @param {number} wait
 * @returns {T & { cancel: () => void }}
 */
export function debounce(callback, wait) {
  /** @type {ReturnType<typeof setTimeout> | undefined} */
  let timeout;
  const debounced = /** @type {T & { cancel: () => void }} */ (
    /** @type {unknown} */ (
      /** @param {Parameters<T>} args */
      (...args) => {
        clearTimeout(timeout);
        timeout = setTimeout(() => callback(...args), wait);
      }
    )
  );
  debounced.cancel = () => clearTimeout(timeout);
  return debounced;
}

/**
 * Runs the callback at most once per animation frame.
 * @template {(...args: any[]) => void} T
 * @param {T} callback
 * @returns {T}
 */
export function throttle(callback) {
  let frame = 0;
  return /** @type {T} */ (
    /** @type {unknown} */ (
      /** @param {Parameters<T>} args */
      (...args) => {
        if (frame) return;
        frame = requestAnimationFrame(() => {
          frame = 0;
          callback(...args);
        });
      }
    )
  );
}

/**
 * Request options for the Ajax API.
 * @param {'json' | 'javascript'} [type]
 * @param {RequestInit} [config]
 * @returns {RequestInit & { headers: Record<string, string> }}
 */
export function fetchConfig(type = 'json', config = {}) {
  /** @type {Record<string, string>} */
  const headers = { 'Content-Type': 'application/json', Accept: `application/${type}` };
  return { method: 'POST', ...config, headers: { ...headers, .../** @type {Record<string, string>} */ (config.headers || {}) } };
}

/** @param {() => void} callback */
export function onDocumentLoaded(callback) {
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', callback, { once: true });
  else callback();
}

/** @returns {MediaQueryList} */
export function mobileMediaQuery() {
  return window.matchMedia(MOBILE_QUERY);
}

/** @returns {boolean} */
export function isMobileBreakpoint() {
  return mobileMediaQuery().matches;
}

/** @returns {boolean} */
export function prefersReducedMotion() {
  return window.matchMedia(REDUCED_MOTION_QUERY).matches;
}

/** @returns {ScrollBehavior} */
export function scrollBehavior() {
  return prefersReducedMotion() ? 'auto' : 'smooth';
}

/**
 * Runs the callback once the element's running CSS animations and transitions finish,
 * or right away when none are running.
 * @param {Element} element
 * @param {() => void} callback
 */
export function onAnimationEnd(element, callback) {
  const animations = element.getAnimations();
  if (!animations.length) {
    callback();
    return;
  }
  Promise.allSettled(animations.map((animation) => animation.finished)).then(callback);
}

/**
 * @param {string} email
 * @returns {boolean}
 */
export function isValidEmail(email) {
  return EMAIL_PATTERN.test(email.trim());
}

/**
 * Restarts a CSS animation tied to a class.
 * @param {Element} element
 * @param {string} className
 */
export function replayClass(element, className) {
  element.classList.remove(className);
  void (/** @type {HTMLElement} */ (element).offsetWidth);
  element.classList.add(className);
}

/**
 * Visible, enabled elements that take keyboard focus.
 * @param {ParentNode} root
 * @returns {HTMLElement[]}
 */
export function focusableElements(root) {
  const selector =
    'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select, textarea, [tabindex]:not([tabindex="-1"])';
  return Array.from(root.querySelectorAll(selector)).filter(
    /** @returns {element is HTMLElement} */
    (element) =>
      element instanceof HTMLElement && (element.getClientRects().length > 0 || element === document.activeElement)
  );
}

/**
 * Keeps Tab and Shift+Tab inside the container.
 * @param {KeyboardEvent} event
 * @param {HTMLElement} container
 */
export function trapFocus(event, container) {
  if (event.key !== 'Tab') return;
  const items = focusableElements(container);
  const first = items[0];
  const last = items[items.length - 1];
  if (!first || !last) return;
  const active = document.activeElement;
  if (event.shiftKey && (active === first || active === container)) {
    event.preventDefault();
    last.focus();
  } else if (!event.shiftKey && active === last) {
    event.preventDefault();
    first.focus();
  }
}

/**
 * Formats cents with the shop money format (`{{ amount }}`, `{{ amount_with_comma_separator }}`, ...).
 * @param {number} cents
 * @param {string} [format]
 * @returns {string}
 */
export function formatMoney(cents, format = window.Theme?.moneyFormat || '€{{amount_with_comma_separator}}') {
  /**
   * @param {number} precision
   * @param {string} thousands
   * @param {string} decimal
   */
  const withDelimiters = (precision, thousands = '.', decimal = ',') => {
    const [whole = '0', fraction] = (cents / 100).toFixed(precision).split('.');
    const grouped = whole.replace(/(\d)(?=(\d{3})+(?!\d))/g, `$1${thousands}`);
    return fraction ? `${grouped}${decimal}${fraction}` : grouped;
  };

  return format.replace(/\{\{\s*(\w+)\s*\}\}/, (_, placeholder) => {
    switch (placeholder) {
      case 'amount':
        return withDelimiters(2, ',', '.');
      case 'amount_no_decimals':
        return withDelimiters(0, ',', '.');
      case 'amount_no_decimals_with_comma_separator':
        return withDelimiters(0, '.', ',');
      case 'amount_with_apostrophe_separator':
        return withDelimiters(2, "'", '.');
      case 'amount_with_comma_separator':
      default:
        return withDelimiters(2, '.', ',');
    }
  });
}

/**
 * Adds a width parameter to a Shopify CDN image URL.
 * @param {string} url
 * @param {number} width
 * @returns {string}
 */
export function imageUrl(url, width) {
  return `${url}${url.includes('?') ? '&' : '?'}width=${width}`;
}

/**
 * True when the visitor came from another page of this shop and can go back.
 * @returns {boolean}
 */
export function canGoBack() {
  try {
    return Boolean(document.referrer) && new URL(document.referrer).origin === location.origin && history.length > 1;
  } catch {
    return false;
  }
}

/**
 * Moves a fixed layer to <body> so no ancestor (transform, overflow, z-index) can trap it under the page.
 * @param {Element | null | undefined} element
 */
export function moveToBody(element) {
  if (element && element.parentElement !== document.body) document.body.appendChild(element);
}

/**
 * Fills `[name]` placeholders in a string from Theme.translations.
 * @param {string} text
 * @param {Record<string, string | number>} values
 * @returns {string}
 */
export function interpolate(text, values) {
  return text.replace(/\[(\w+)\]/g, (match, key) => (key in values ? String(values[key]) : match));
}

/* Slideshow and scrolling helpers (slideshow.js, scrolling.js), ported from Horizon. */

/** Desktop media query: the complement of the mobile breakpoint */
export const mediaQueryLarge = window.matchMedia('(min-width: 768px)');

/**
 * The view transition in progress. The theme starts none, so this stays undefined;
 * components still await it so they work if one is added later.
 * @type {{ current: Promise<void> | undefined }}
 */
export const viewTransition = {
  current: undefined,
};

/**
 * @param {Element} [element]
 * @returns {boolean}
 */
export function isRTL(element = document.documentElement) {
  return getComputedStyle(element).direction === 'rtl';
}

/**
 * @param {number} value
 * @param {number} min
 * @param {number} max
 * @returns {number}
 */
export function clamp(value, min, max) {
  return Math.max(min, Math.min(value, max));
}

/**
 * Center point of an element on one axis, in viewport coordinates.
 * @param {Element} element
 * @param {'x' | 'y'} axis
 * @returns {number}
 */
export function center(element, axis) {
  const { left, width, top, height } = element.getBoundingClientRect();
  return axis === 'x' ? left + width / 2 : top + height / 2;
}

/**
 * The value closest to the target.
 * @param {number[]} values
 * @param {number} target
 * @returns {number}
 */
export function closest(values, target) {
  return values.reduce((prev, curr) => (Math.abs(curr - target) < Math.abs(prev - target) ? curr : prev));
}

/** @param {Event} event */
export function preventDefault(event) {
  event.preventDefault();
}

/** Batches DOM work: queued tasks run after the next animation frame. */
class Scheduler {
  /** @type {Set<() => void>} */
  #queue = new Set();
  #scheduled = false;

  /** @param {() => void} task */
  schedule = async (task) => {
    this.#queue.add(task);

    if (!this.#scheduled) {
      this.#scheduled = true;

      if (viewTransition.current) await viewTransition.current;

      requestAnimationFrame(this.flush);
    }
  };

  flush = () => {
    for (const task of this.#queue) {
      setTimeout(task, 0);
    }

    this.#queue.clear();
    this.#scheduled = false;
  };
}

export const scheduler = new Scheduler();
